// Sessions-world presets (v3 OrgAndSessionSelect / useSessionSelect wires,
// D3 sessions run). Two hooks:
//   - useScopedChildOrgsSelect — generic-pair props for the privilege-scoped
//     child-org picker (v3 OrgAndSessionSelect's OrgSelect wire: children of
//     the current user's org, nested_children grouped)
//   - useSessionsSelect — option-list preset for the per-experience session
//     picker (v3 useSessionSelect wire; "All Sessions" sentinel = 'all')
// Consumers: GradeToolbar, MonitorTab, export session-select, deliver
// SessionsManager (org filter) — the 4-consumer map in antd-strip-inventory.
import type { BaseRecord, CrudFilter } from '@refinedev/core';
import { useList } from '@refinedev/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { APMultiSelectOption, APMultiSelectOptionGroup } from '@/components/ap/form';
import { useCurrentUser } from '@/hooks/dataHelpers';
import { selectExperience, useExperienceStore } from '@/store/experienceStore';

import { attrs, type JsonApiRow } from './jsonApi';

/** v3 useOrgSelect nested_children branch parity: a parent with children
 *  becomes a HEADING (not selectable) over its selectable children; childless
 *  parents are loose selectable options. v4 delta (flagged): the heading shows
 *  the parent's NAME — v3 read `originalItem.label`, which orgs don't have, so
 *  the antd optgroup heading rendered blank. Id-less rows are skipped. */
export const groupNestedChildOrgOptions = (records: BaseRecord[]): APMultiSelectOptionGroup[] =>
  records.flatMap((record) => {
    const org = attrs(record as JsonApiRow);
    if (org.id === undefined || org.id === null) return [];
    const children: Array<Record<string, any>> = Array.isArray(org.nested_children)
      ? org.nested_children
      : [];
    if (children.length > 0) {
      const options = children
        .filter((child) => child?.id !== undefined && child?.id !== null)
        .map((child) => ({ value: child.id, label: String(child?.name ?? '') }));
      return options.length > 0 ? [{ label: String(org.name ?? ''), options }] : [];
    }
    return [{ options: [{ value: org.id, label: String(org.name ?? '') }] }];
  });

export interface ScopedChildOrgsSelectProps {
  resource: 'orgs';
  optionLabel: string;
  optionValue: string;
  pageSize: number;
  filters: CrudFilter[];
  transformGroups: (records: BaseRecord[]) => APMultiSelectOptionGroup[];
}

/** Generic-pair props for the child-org picker scoped to the current user's
 *  org (v3 OrgAndSessionSelect wire: by_parent_id + unarchived +
 *  with_nested_children + without_org_programmes, page_size 5). Spread into
 *  APMultiSelect. Accessors stay JSON:API for hydrated-value resolution. */
export const useScopedChildOrgsSelect = (): ScopedChildOrgsSelectProps => {
  const currentUser = useCurrentUser();
  const parentOrgId = currentUser?.org?.id;
  return useMemo(
    () => ({
      resource: 'orgs',
      optionLabel: 'attributes.name',
      optionValue: 'attributes.id',
      pageSize: 5,
      filters: [
        { field: 'by_parent_id', operator: 'eq', value: parentOrgId },
        { field: 'unarchived', operator: 'eq', value: true },
        { field: 'with_nested_children', operator: 'eq', value: true },
        { field: 'without_org_programmes', operator: 'eq', value: true },
      ],
      transformGroups: groupNestedChildOrgOptions,
    }),
    [parentOrgId]
  );
};

export interface UseSessionsSelectOptions {
  /** Scope sessions to these orgs; falls back to the current user's org (v3). */
  orgIds?: Array<string | number>;
  /** BE status filter. v3 useSessionSelect default. */
  statuses?: string[];
  /** Drop the leading "All Sessions" option ('all' sentinel). */
  hideAllSessionsOption?: boolean;
  /** Already-translated label for the 'all' option (v3 firstOptionLabel). */
  firstOptionLabel?: string;
  /** Only sessions with a student-pack export (export modal variant). */
  withStudentPackExport?: boolean;
  /** Extra filters (search / page / by_mode — export modal variant). */
  filters?: CrudFilter[];
  /** v3 useCustomSelect page_size filter (default 15). */
  pageSize?: number;
  refetchOnMount?: boolean | 'always';
  enabled?: boolean;
}

export interface SessionsSelect {
  sessions: any[];
  /** 'all' option first (unless hidden), then the fetched sessions. */
  options: APMultiSelectOption[];
  isFetching: boolean;
  shouldShowAllSessions: boolean;
  total: number;
}

/** Option-list preset for the per-experience session picker. Exact v3
 *  useSessionSelect wire: experience_sessions filtered by by_experience_id
 *  (store experience) + unarchived + by_statuses + by_org_ids, page_size as a
 *  filter, no page params (select-style query). Rows arrive FLAT
 *  (formatJsonApiData — v3 parity), so callers can read settings/students. */
export const useSessionsSelect = (options: UseSessionsSelectOptions = {}): SessionsSelect => {
  const {
    orgIds = [],
    statuses = ['published', 'closed'],
    hideAllSessionsOption = false,
    firstOptionLabel,
    withStudentPackExport = false,
    filters = [],
    pageSize = 15,
    refetchOnMount = false,
    enabled = true,
  } = options;
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const experience = useExperienceStore(selectExperience);
  const experienceId = experience?.id;

  const shouldShowAllSessions = !hideAllSessionsOption;
  const orgIdsToUse = orgIds.length > 0 ? orgIds : [currentUser.org_id];

  const { data, isFetching } = useList({
    resource: 'experience_sessions',
    filters: [
      ...filters,
      { field: 'page_size', operator: 'eq', value: pageSize },
      { field: 'by_experience_id', operator: 'eq', value: experienceId },
      { field: 'unarchived', operator: 'eq', value: true },
      { field: 'by_statuses', operator: 'eq', value: statuses },
      { field: 'by_org_ids', operator: 'eq', value: orgIdsToUse },
      ...(withStudentPackExport
        ? [{ field: 'with_student_pack_export', operator: 'eq' as const, value: true }]
        : []),
    ],
    // v3 used a select-style query (no page/page_size pagination params —
    // the page_size FILTER carries the size).
    pagination: { mode: 'off' },
    queryOptions: { enabled: enabled && !!experienceId, refetchOnMount },
    meta: { formatJsonApiData: true },
  });

  const rows = data?.data;
  const sessions = useMemo(() => rows ?? [], [rows]);
  const total = data?.total ?? sessions.length;

  const selectOptions = useMemo(
    () => [
      ...(shouldShowAllSessions
        ? [{ label: firstOptionLabel ?? t('CommonText.all_sessions'), value: 'all' }]
        : []),
      ...sessions.map((session: any) => ({
        value: session.id,
        label: String(session.name ?? session.id),
      })),
    ],
    [shouldShowAllSessions, firstOptionLabel, sessions, t]
  );

  return {
    sessions,
    options: selectOptions,
    isFetching: !!isFetching,
    shouldShowAllSessions,
    total,
  };
};
